import React, { useState, useEffect } from 'react';
import { 
  Home,
  Wifi,
  Coffee,
  Tv,
  Wind,
  Lightbulb,
  Volume2,
  Droplets,
  Sun,
  Moon,
  Zap,
  MessageSquare,
  Bell,
  Heart,
  Clock,
  MapPin,
  Utensils,
  Car,
  ShowerHead,
  Sparkles,
  ChevronUp,
  ChevronDown,
  Plus,
  Minus,
  Phone,
  HelpCircle,
  Camera,
  Music,
  Smartphone,
  Battery,
  ThermometerSun,
  Users,
  Shield,
  Star
} from 'lucide-react';

interface RoomControl {
  id: string;
  name: string;
  icon: React.ElementType;
  value: number | boolean;
  type: 'toggle' | 'slider' | 'button';
  unit?: string;
}

interface ServiceRequest {
  id: string;
  name: string;
  icon: React.ElementType;
  eta?: string;
  popular?: boolean;
}

interface LocalSecret {
  id: string;
  name: string;
  type: 'beach' | 'restaurant' | 'activity' | 'sunset';
  distance: string;
  rating: number;
  insider?: string;
}

export default function InRoomExperience() {
  const [activeMode, setActiveMode] = useState<'relax' | 'work' | 'sleep' | 'custom'>('relax');
  const [temperature, setTemperature] = useState(22);
  const [lighting, setLighting] = useState(70);
  const [curtains, setCurtains] = useState(true);
  const [doNotDisturb, setDoNotDisturb] = useState(false);
  const [notifications, setNotifications] = useState(2);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [batteryLevel] = useState(85);
  const [wifiStrength] = useState(4);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const roomModes = {
    relax: { temp: 22, light: 70, curtains: true, music: 'Bossa Nova' },
    work: { temp: 20, light: 100, curtains: true, music: 'Focus' },
    sleep: { temp: 18, light: 0, curtains: false, music: 'Ocean Sounds' },
    custom: { temp: temperature, light: lighting, curtains: curtains, music: 'Your Mix' }
  };

  const quickServices: ServiceRequest[] = [
    { id: '1', name: 'Fresh Towels', icon: Droplets, eta: '5 min', popular: true },
    { id: '2', name: 'Room Service', icon: Utensils, eta: '20 min' },
    { id: '3', name: 'Extra Pillows', icon: Moon, eta: '3 min' },
    { id: '4', name: 'Beach Shuttle', icon: Car, eta: '15 min', popular: true },
    { id: '5', name: 'Housekeeping', icon: Sparkles, eta: '10 min' },
    { id: '6', name: 'Concierge', icon: Users, eta: 'Now' }
  ];

  const localSecrets: LocalSecret[] = [
    { 
      id: '1', 
      name: 'Praia Escondida', 
      type: 'beach', 
      distance: '5 min walk', 
      rating: 4.9,
      insider: 'Go at 6am for dolphins'
    },
    {
      id: '2',
      name: 'Casa da Moqueca',
      type: 'restaurant',
      distance: '10 min walk',
      rating: 4.8,
      insider: 'Ask for the off-menu fish'
    },
    {
      id: '3',
      name: 'Sunset Point',
      type: 'sunset',
      distance: '3 min walk',
      rating: 5.0,
      insider: 'Best 30 min before sunset'
    }
  ];

  const applyMode = (mode: 'relax' | 'work' | 'sleep' | 'custom') => {
    setActiveMode(mode);
    if (mode !== 'custom') {
      const settings = roomModes[mode];
      setTemperature(settings.temp);
      setLighting(settings.light);
      setCurtains(settings.curtains);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Status Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-lg px-4 py-2">
        <div className="flex justify-between items-center text-white text-sm">
          <div className="flex items-center gap-3">
            <span className="font-medium">Room 12</span>
            <div className="flex items-center gap-1">
              <Wifi className="h-3 w-3" />
              {[...Array(wifiStrength)].map((_, i) => (
                <div key={i} className="w-1 h-2 bg-white rounded-sm" />
              ))}
            </div>
          </div>
          <span className="font-mono">{currentTime.toLocaleTimeString()}</span>
          <div className="flex items-center gap-2">
            {notifications > 0 && (
              <div className="relative">
                <Bell className="h-4 w-4" />
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
              </div>
            )}
            <Battery className="h-4 w-4" />
            <span>{batteryLevel}%</span>
          </div>
        </div>
      </div>

      <div className="pt-12 pb-20 px-4">
        {/* Welcome Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">
            Good {currentTime.getHours() < 12 ? 'Morning' : currentTime.getHours() < 18 ? 'Afternoon' : 'Evening'}, Maria
          </h1>
          <p className="text-blue-200">Everything is ready for your perfect stay</p>
        </div>

        {/* Room Modes */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 mb-6">
          <h2 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-400" />
            Room Atmosphere
          </h2>
          <div className="grid grid-cols-4 gap-2">
            {(['relax', 'work', 'sleep', 'custom'] as const).map((mode) => (
              <button
                key={mode}
                onClick={() => applyMode(mode)}
                className={`py-3 px-2 rounded-xl font-medium capitalize transition-all ${
                  activeMode === mode 
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' 
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {mode === 'relax' && <Coffee className="h-5 w-5 mx-auto mb-1" />}
                {mode === 'work' && <Zap className="h-5 w-5 mx-auto mb-1" />}
                {mode === 'sleep' && <Moon className="h-5 w-5 mx-auto mb-1" />}
                {mode === 'custom' && <Settings className="h-5 w-5 mx-auto mb-1" />}
                {mode}
              </button>
            ))}
          </div>

          {/* Room Controls */}
          <div className="mt-4 space-y-3">
            {/* Temperature */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ThermometerSun className="h-5 w-5 text-orange-400" />
                <span className="text-white">Temperature</span>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setTemperature(Math.max(16, temperature - 1))}
                  className="p-1 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <Minus className="h-4 w-4 text-white" />
                </button>
                <span className="text-white font-mono w-12 text-center">{temperature}°C</span>
                <button 
                  onClick={() => setTemperature(Math.min(28, temperature + 1))}
                  className="p-1 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                >
                  <Plus className="h-4 w-4 text-white" />
                </button>
              </div>
            </div>

            {/* Lighting */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-400" />
                <span className="text-white">Lighting</span>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={lighting}
                  onChange={(e) => setLighting(Number(e.target.value))}
                  className="w-24 accent-yellow-400"
                />
                <span className="text-white text-sm w-10">{lighting}%</span>
              </div>
            </div>

            {/* Curtains */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {curtains ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5 text-blue-400" />}
                <span className="text-white">Curtains</span>
              </div>
              <button
                onClick={() => setCurtains(!curtains)}
                className={`px-4 py-1 rounded-lg font-medium transition-all ${
                  curtains ? 'bg-yellow-400/20 text-yellow-300' : 'bg-blue-400/20 text-blue-300'
                }`}
              >
                {curtains ? 'Open' : 'Closed'}
              </button>
            </div>

            {/* Do Not Disturb */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-red-400" />
                <span className="text-white">Do Not Disturb</span>
              </div>
              <button
                onClick={() => setDoNotDisturb(!doNotDisturb)}
                className={`px-4 py-1 rounded-lg font-medium transition-all ${
                  doNotDisturb ? 'bg-red-400/20 text-red-300' : 'bg-white/10 text-white/50'
                }`}
              >
                {doNotDisturb ? 'On' : 'Off'}
              </button>
            </div>
          </div>
        </div>

        {/* Quick Services */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 mb-6">
          <h2 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Bell className="h-5 w-5 text-purple-400" />
            Quick Services
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {quickServices.map((service) => {
              const Icon = service.icon;
              return (
                <button
                  key={service.id}
                  className="bg-white/10 hover:bg-white/20 transition-all rounded-xl p-3 text-left relative group"
                >
                  {service.popular && (
                    <span className="absolute top-2 right-2 text-xs bg-yellow-400/20 text-yellow-300 px-2 py-0.5 rounded-full">
                      Popular
                    </span>
                  )}
                  <Icon className="h-6 w-6 text-purple-400 mb-2" />
                  <p className="text-white font-medium text-sm">{service.name}</p>
                  {service.eta && (
                    <p className="text-blue-200 text-xs mt-1 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {service.eta}
                    </p>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/0 to-blue-600/0 group-hover:from-purple-600/10 group-hover:to-blue-600/10 rounded-xl transition-all" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Local Secrets */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 mb-6">
          <h2 className="text-white font-semibold mb-3 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-green-400" />
            Today's Local Secrets
          </h2>
          <div className="space-y-3">
            {localSecrets.map((secret) => (
              <div
                key={secret.id}
                className="bg-white/10 rounded-xl p-3 hover:bg-white/20 transition-all cursor-pointer"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-white font-medium">{secret.name}</h3>
                    <p className="text-blue-200 text-sm flex items-center gap-2 mt-1">
                      <MapPin className="h-3 w-3" />
                      {secret.distance}
                      <span className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        {secret.rating}
                      </span>
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg ${
                    secret.type === 'beach' ? 'bg-blue-400/20' :
                    secret.type === 'restaurant' ? 'bg-orange-400/20' :
                    secret.type === 'sunset' ? 'bg-purple-400/20' :
                    'bg-green-400/20'
                  }`}>
                    {secret.type === 'beach' && <Waves className="h-4 w-4 text-blue-400" />}
                    {secret.type === 'restaurant' && <Utensils className="h-4 w-4 text-orange-400" />}
                    {secret.type === 'sunset' && <Sun className="h-4 w-4 text-purple-400" />}
                    {secret.type === 'activity' && <Camera className="h-4 w-4 text-green-400" />}
                  </div>
                </div>
                {secret.insider && (
                  <div className="bg-yellow-400/10 rounded-lg px-3 py-2">
                    <p className="text-yellow-300 text-xs flex items-start gap-1">
                      <Sparkles className="h-3 w-3 mt-0.5" />
                      <span>Insider tip: {secret.insider}</span>
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Emergency & Help */}
        <div className="grid grid-cols-2 gap-3">
          <button className="bg-white/10 backdrop-blur-lg rounded-xl p-4 hover:bg-white/20 transition-all flex items-center gap-3">
            <Phone className="h-5 w-5 text-green-400" />
            <div className="text-left">
              <p className="text-white font-medium">Call Reception</p>
              <p className="text-blue-200 text-xs">Available 24/7</p>
            </div>
          </button>
          <button className="bg-white/10 backdrop-blur-lg rounded-xl p-4 hover:bg-white/20 transition-all flex items-center gap-3">
            <MessageSquare className="h-5 w-5 text-blue-400" />
            <div className="text-left">
              <p className="text-white font-medium">Message Us</p>
              <p className="text-blue-200 text-xs">WhatsApp</p>
            </div>
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-black/50 backdrop-blur-lg border-t border-white/10">
        <div className="flex justify-around py-2">
          <button className="flex flex-col items-center gap-1 py-2 px-4 text-purple-400">
            <Home className="h-5 w-5" />
            <span className="text-xs">Room</span>
          </button>
          <button className="flex flex-col items-center gap-1 py-2 px-4 text-white/50">
            <Utensils className="h-5 w-5" />
            <span className="text-xs">Dining</span>
          </button>
          <button className="flex flex-col items-center gap-1 py-2 px-4 text-white/50">
            <MapPin className="h-5 w-5" />
            <span className="text-xs">Explore</span>
          </button>
          <button className="flex flex-col items-center gap-1 py-2 px-4 text-white/50">
            <Heart className="h-5 w-5" />
            <span className="text-xs">Saved</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Fix missing imports
const Waves = MapPin;
const Settings = Smartphone;